import React, { useState, useEffect, useCallback, useRef } from 'react';
import SpinnerOverlay from '../Spinner/spinneroverlay';
import { Link, useLocation } from 'react-router-dom';
import './ObjectForm.css';
import config from '../../config';
import UnifiedFileUpload from '../Seller/UnifiedFileUpload';
import axios from '../../axios';
import { getSourceDomain, getValuationMarket, trackFormSubmission } from '../../utils/analytics';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faHome } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../i18n/LanguageContext';
import { propertyTypeOptions, translatePropertyType } from '../../i18n/translations';
import { getMapboxCountry, getMapboxLanguage } from '../../utils/mapboxCountry';

// Import our new components
import FacilitiesSelector from './FacilitiesSelector';
import PropertyFields from './PropertyFields';
import ImageModal from './ImageModal';

// Import BankID logo
import BankIdLogo from '../../bankid/BankID_logo_white.svg';

// Use the Mapbox token from config.js
const MAPBOX_TOKEN = config.mapbox_token;

const ObjectForm = ({ handleSubmit, object, setObject, images, setImages, workflowType = 'ai' }) => {
  const location = useLocation();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState('');
  const [salesRequirements, setSalesRequirements] = useState('');
  const [hasSalesRequirements, setHasSalesRequirements] = useState(false);
  const [renovations, setRenovations] = useState('');
  const [hasRenovations, setHasRenovations] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [previewUrls, setPreviewUrls] = useState([]);
  
  // Add validation errors state
  const [validationErrors, setValidationErrors] = useState({});
  
  // Add refs for the arrow buttons to check if they're visible
  const leftArrowRef = useRef(null);
  const rightArrowRef = useRef(null);

  // Check login status
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Base state for common fields
  const [address, setAddress] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [numberOfRooms, setNumberOfRooms] = useState('');
  const [area, setArea] = useState('');
  const [displayContactInfo, setDisplayContactInfo] = useState(false);
  const [email, setEmail] = useState('');
  
  // Check if email is provided via URL parameter
  const urlParams = new URLSearchParams(location.search);
  const emailFromUrl = urlParams.get('email');
  const addressFromUrl = urlParams.get('address');
  const hideEmailField = Boolean(emailFromUrl);

  // State for geolocation and suggestions
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [lan, setLan] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);

  // Property type-specific fields
  // Apartment (Lägenhet)
  const [floor, setFloor] = useState('');
  const [hasElevator, setHasElevator] = useState(false);
  const [monthlyFee, setMonthlyFee] = useState('');
  const [balconyType, setBalconyType] = useState('');
  
  // House (Villa) and Vacation home (Fritidshus)
  const [plotSize, setPlotSize] = useState('');
  const [constructionYear, setConstructionYear] = useState('');
  const [numberOfFloors, setNumberOfFloors] = useState('');
  const [hasGarage, setHasGarage] = useState(false);
  const [heatingSystem, setHeatingSystem] = useState('');
  
  // Nytt tillstånd för faciliteter
  const [facilities, setFacilities] = useState({
    // Lägenhet
    has_elevator: false,
    has_storage: false,
    has_bike_room: false,
    has_laundry_room: false,
    has_broadband: false,
    has_courtyard: false,
    // Villa/Radhus/etc
    has_garage: false,
    has_carport: false,
    has_patio: false,
    has_parking: false,
    has_fireplace: false,
    // Fritidshus
    has_municipal_water: false,
    has_water_proximity: false,
    has_boat_dock: false,
    has_winterized: false
  });

  // Check if current workflow is slim
  const isSlimWorkflow = workflowType === 'slim';

  // Function to track address input
  const trackAddressInput = async (address, pageSource) => {
    try {
      await axios.post(`${config.backend_url}/api/track-address`, {
        address,
        page_source: pageSource
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Address tracking failed:', error);
    }
  };

  // Initialize email from URL parameter
  useEffect(() => {
    if (emailFromUrl) {
      setEmail(decodeURIComponent(emailFromUrl));
    }
    if (addressFromUrl) {
      setAddress(decodeURIComponent(addressFromUrl));
    }
  }, [emailFromUrl, addressFromUrl]);

  // Check login status
  useEffect(() => {
    const checkLoginStatus = () => {
      const loggedIn = localStorage.getItem('loggedIn') === 'true';
      setIsLoggedIn(loggedIn);
    };

    checkLoginStatus();

    // Listen for storage changes to update login status if it changes in another tab
    window.addEventListener('storage', checkLoginStatus);
    
    return () => {
      window.removeEventListener('storage', checkLoginStatus);
    };
  }, []);

  // Validation patterns
  const EMAIL_PATTERN = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  // Validation functions

  const validateAddress = (address) => {
    if (!address) {
      return t('objectForm.validation.addressRequired');
    }
    if (address.length < 5) {
      return t('objectForm.validation.addressLength');
    }
    return '';
  };

  const validatePropertyType = (propertyType) => {
    if (!propertyType) {
      return t('objectForm.validation.propertyRequired');
    }
    return '';
  };

  const validateRooms = (rooms, propertyType) => {
    if (propertyType === 'Tomt' || propertyType === 'Gård/skog') {
      return '';
    }
    if (!rooms) {
      return t('objectForm.validation.roomsRequired');
    }
    return '';
  };

  const validateArea = (area, propertyType) => {
    if (!area) {
      return t('objectForm.validation.areaRequired');
    }
    const areaNum = parseInt(area);
    if (isNaN(areaNum) || areaNum < 1 || areaNum > 10000) {
      return t('objectForm.validation.areaRange');
    }
    return '';
  };

  const validateDescription = (description) => {
    if (!description) {
      return t('objectForm.validation.descriptionRequired');
    }
    if (description.length < 10) {
      return t('objectForm.validation.descriptionLength');
    }
    if (description.length > 5000) {
      return t('objectForm.validation.descriptionMax');
    }
    return '';
  };

  const validateFloor = (floor) => {
    if (!floor) return '';
    const floorNum = parseInt(floor);
    if (isNaN(floorNum) || floorNum < 0 || floorNum > 50) {
      return t('objectForm.validation.floorRange');
    }
    return '';
  };

  const validateMonthlyFee = (fee) => {
    if (!fee) return '';
    const feeNum = parseInt(fee.replace(/[\s\,kr\.]/g, ''));
    if (isNaN(feeNum) || feeNum < 0 || feeNum > 100000) {
      return t('objectForm.validation.feeRange');
    }
    return '';
  };

  const validatePlotSize = (size) => {
    if (!size) return '';
    const sizeNum = parseInt(size);
    if (isNaN(sizeNum) || sizeNum < 1 || sizeNum > 1000000) {
      return t('objectForm.validation.plotRange');
    }
    return '';
  };

  const validateConstructionYear = (year) => {
    if (!year) return '';
    const yearNum = parseInt(year);
    if (isNaN(yearNum) || yearNum < 1800 || yearNum > 2030) {
      return t('objectForm.validation.yearRange');
    }
    return '';
  };

  const validateEmail = (email) => {
    // If email is provided via URL, we don't need to validate it here
    if (hideEmailField && emailFromUrl) {
      return '';
    }
    if (isSlimWorkflow && !email) {
      return t('objectForm.validation.emailRequired');
    }
    if (!email) return '';
    if (!EMAIL_PATTERN.test(email)) {
      return t('objectForm.validation.emailInvalid');
    }
    return '';
  };

  // Function to validate all fields
  const validateAllFields = () => {
    const errors = {};
    
    errors.address = validateAddress(address);
    errors.propertyType = validatePropertyType(propertyType);
    errors.numberOfRooms = validateRooms(numberOfRooms, propertyType);
    errors.area = validateArea(area, propertyType);
    
    // Email validation for slim workflow
    if (isSlimWorkflow && !hideEmailField) {
      errors.email = validateEmail(email);
    }
    
    // For slim workflow, only validate basic description if provided, not required
    if (isSlimWorkflow) {
      // Only validate description if it's provided
      if (object && object.trim()) {
        errors.object = validateDescription(object);
      }
    } else {
      // For full workflow, description is required
      errors.object = validateDescription(object);
      
      // Property-specific validations only for non-slim workflows
      if (propertyType === 'Lägenhet') {
        errors.floor = validateFloor(floor);
        errors.monthlyFee = validateMonthlyFee(monthlyFee);
      } else if (['Villa', 'Radhus', 'Fritidsboende', 'Gård/skog'].includes(propertyType)) {
        errors.plotSize = validatePlotSize(plotSize);
        errors.constructionYear = validateConstructionYear(constructionYear);
      }
    }

    // Remove empty errors
    Object.keys(errors).forEach(key => {
      if (!errors[key]) {
        delete errors[key];
      }
    });

    setValidationErrors(errors);
    
    // If there are errors, scroll to the first one
    if (Object.keys(errors).length > 0) {
      scrollToFirstError(errors);
    }
    
    return Object.keys(errors).length === 0;
  };

  // Function to scroll to the first error field
  const scrollToFirstError = (errors) => {
    // Define the order of fields as they appear in the form
    const fieldOrder = [
      'address',
      'propertyType', 
      'numberOfRooms',
      'area',
      'email',
      'floor',
      'monthlyFee',
      'plotSize',
      'constructionYear',
      'object'
    ];
    
    // Find the first field with an error
    const firstErrorField = fieldOrder.find(field => errors[field]);
    
    if (firstErrorField) {
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        // Focus the field after scrolling
        setTimeout(() => {
          element.focus();
        }, 300);
      }
    }
  };

  // Function to clear validation error for a specific field
  const clearValidationError = (fieldName) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  // Enhanced input change handlers that clear validation errors
  const handleAddressChangeWithValidation = (e) => {
    const value = e.target.value;
    setAddress(value);
    clearValidationError('address');
    handleAddressChange(e); // Call original address change handler
  };

  const handlePropertyTypeChange = (e) => {
    const value = e.target.value;
    setPropertyType(value);
    clearValidationError('propertyType');
    // Clear room and area errors when property type changes
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.numberOfRooms;
      delete newErrors.area;
      delete newErrors.propertyType;
      return newErrors;
    });
  };

  const handleObjectChange = (e) => {
    const value = e.target.value;
    setObject(value);
    clearValidationError('object');
  };

  const handleNumberOfRoomsChange = (e) => {
    const value = e.target.value;
    setNumberOfRooms(value);
    clearValidationError('numberOfRooms');
  };

  const handleAreaChange = (e) => {
    const value = e.target.value;
    setArea(value);
    clearValidationError('area');
  };

  // Property-specific field handlers that clear validation errors
  const handleFloorChange = (value) => {
    setFloor(value);
    clearValidationError('floor');
  };

  const handleMonthlyFeeChange = (value) => {
    setMonthlyFee(value);
    clearValidationError('monthlyFee');
  };

  const handlePlotSizeChange = (value) => {
    setPlotSize(value);
    clearValidationError('plotSize');
  };

  const handleConstructionYearChange = (value) => {
    setConstructionYear(value);
    clearValidationError('constructionYear');
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    clearValidationError('email');
  };

  // Component to display validation errors
  const ValidationErrorMessage = ({ fieldName }) => {
    const error = validationErrors[fieldName];
    if (!error) return null;
    
    return (
      <div className="validation-error">
        {error}
      </div>
    );
  };
  
  // Reset specific fields when property type changes
  useEffect(() => {
    // Reset all property-specific fields when type changes
    setFloor('');
    setHasElevator(false);
    setMonthlyFee('');
    setBalconyType('');
    setPlotSize('');
    setConstructionYear('');
    // Only set default number of floors for non-slim workflows
    setNumberOfFloors(isSlimWorkflow ? '' : '1');
    setHasGarage(false);
    setHeatingSystem('');
    
    // Clear validation errors for property-specific fields
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.floor;
      delete newErrors.monthlyFee;
      delete newErrors.plotSize;
      delete newErrors.constructionYear;
      return newErrors;
    });
    
    // Återställ faciliteter också
    setFacilities({
      // Lägenhet
      has_elevator: false,
      has_storage: false,
      has_bike_room: false,
      has_laundry_room: false,
      has_broadband: false,
      has_courtyard: false,
      // Villa/Radhus/etc
      has_garage: false,
      has_carport: false,
      has_patio: false,
      has_parking: false,
      has_fireplace: false,
      // Fritidshus
      has_municipal_water: false,
      has_water_proximity: false,
      has_boat_dock: false,
      has_winterized: false
    });
  }, [propertyType]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize uploaded images if they exist
  useEffect(() => {
    if (images && images.length > 0) {
      setUploadedImages(images);
    }
  }, [images]);

  // Handle file changes from UnifiedFileUpload
  const handleFilesChange = (files) => {
    setSelectedFiles(files);
    // Clear image error when files are selected
    if (files && files.length > 0) {
      setImageError('');
    }
  };

  // Handle preview URL changes from UnifiedFileUpload
  const handlePreviewUrlsChange = (urls) => {
    setPreviewUrls(urls);
  };

  // Handle images update from UnifiedFileUpload
  const handleImagesUpdate = (newImages) => {
    setUploadedImages(newImages);
    setImages(newImages);
  };

  // Get all available images - both uploaded and preview URLs
  const getAllImages = useCallback(() => {
    return [...uploadedImages, ...previewUrls];
  }, [uploadedImages, previewUrls]);

  // Full-size image modal
  const handleImageClick = (imgUrl) => {
    const allImages = getAllImages();
    const index = allImages.findIndex(img => img === imgUrl);
    if (index === -1) {
      console.error("Image not found in allImages array:", imgUrl);
      return;
    }
    setSelectedImageIndex(index);
    setSelectedImage(imgUrl);
  };

  // Navigate to previous image - implement with useCallback
  const handlePrevImage = useCallback(() => {
    const allImages = getAllImages();
    if (selectedImageIndex > 0) {
      const newIndex = selectedImageIndex - 1;
      setSelectedImageIndex(newIndex);
      setSelectedImage(allImages[newIndex]);
    }
  }, [selectedImageIndex, getAllImages]);

  // Navigate to next image - implement with useCallback
  const handleNextImage = useCallback(() => {
    const allImages = getAllImages();
    if (selectedImageIndex < allImages.length - 1) {
      const newIndex = selectedImageIndex + 1;
      setSelectedImageIndex(newIndex);
      setSelectedImage(allImages[newIndex]);
    }
  }, [selectedImageIndex, getAllImages]);

  // Handle keyboard navigation for image modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!selectedImage) return;
      
      if (event.key === 'ArrowLeft') {
        handlePrevImage();
      } else if (event.key === 'ArrowRight') {
        handleNextImage();
      } else if (event.key === 'Escape') {
        setSelectedImage(null);
        setSelectedImageIndex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage, handlePrevImage, handleNextImage]);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  // Handle address change with autocomplete
  const handleAddressChange = async (e) => {
    const value = e.target.value;
    setAddress(value);

    // Only search if the value is sufficiently long
    if (value.length > 3) {
      try {
        const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            value
          )}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&country=${getMapboxCountry(language)}&language=${getMapboxLanguage(language)}`
        );
        const data = await response.json();
        setAddressSuggestions(data.features);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  // When a suggestion is clicked, update address and geolocation
  const handleSuggestionClick = async (feature) => {
    setAddress(feature.place_name);
    // Mapbox returns [lng, lat]
    const longitude = feature.center[0];
    const latitude = feature.center[1];
    setLongitude(longitude);
    setLatitude(latitude);
    
    // Extract län from Mapbox context - look for 'region' place type
    let extractedLan = '';
    if (feature.context) {
      // Look for region context
      for (const context of feature.context) {
        if (context.id && context.id.startsWith('region')) {
          extractedLan = context.text || '';
          // Remove "län" suffix if present and standardize (same as populate_lan_field.py)
          extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
          break;
        }
      }
    }
    
    // Also check if the feature itself is a region (same logic as populate_lan_field.py)
    if (!extractedLan && feature.place_type && feature.place_type.includes('region')) {
      extractedLan = feature.text || '';
      extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
    }
    
    // If no län found, try reverse geocoding with coordinates (same as Python script)
    if (!extractedLan) {
      try {
        const reverseResponse = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&country=${getMapboxCountry(language)}&types=region&language=${getMapboxLanguage(language)}`
        );
        
        if (reverseResponse.ok) {
          const reverseData = await reverseResponse.json();
          if (reverseData.features && reverseData.features.length > 0) {
            for (const reverseFeature of reverseData.features) {
              if (reverseFeature.place_type && reverseFeature.place_type.includes('region')) {
                extractedLan = reverseFeature.text || '';
                extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
                break;
              }
            }
          }
        }
      } catch (reverseError) {
        console.warn('Reverse geocoding failed:', reverseError);
      }
    }
    
    setLan(extractedLan);
    setAddressSuggestions([]);
    clearValidationError('address');
  };

  // Handle closing the image modal
  const handleCloseModal = () => {
    setSelectedImage(null);
    setSelectedImageIndex(null);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setImageError('');

    // Validate all fields before submission
    if (!validateAllFields()) {
      setLoading(false);
      return;
    }

    // Create the form data
    const formData = {
      address,
      property_type: propertyType,
      object: isSlimWorkflow && !object ? t('objectForm.autoDescription') : object,
      display_contact_info: displayContactInfo ? 'true' : 'false',
      latitude: latitude || '',
      longitude: longitude || '',
      lan: lan || '',
      workflow_type: workflowType,
      source_domain: getSourceDomain(),
      valuation_market: getValuationMarket(),
    };

    // Only include sales requirements and renovations for non-slim workflows
    if (!isSlimWorkflow) {
      formData.sales_requirements = hasSalesRequirements ? salesRequirements : t('objectForm.noSalesRequirements');
      formData.renovations = hasRenovations ? renovations : t('objectForm.noRenovations');
    }
    
    // Add email for slim workflow
    if (isSlimWorkflow) {
      // Use email from URL if available, otherwise use the form input
      formData.email = emailFromUrl ? decodeURIComponent(emailFromUrl) : email;
    }
    
    // Lägg alltid till boyta
    formData.area = area;
    
    // Lägg bara till antal rum om bostadstypen inte är Tomt eller Gård/skog
    if (propertyType !== 'Tomt' && propertyType !== 'Gård/skog') {
      formData.number_of_rooms = numberOfRooms;
    }

    // Add property type-specific fields to formData only for non-slim workflows
    if (!isSlimWorkflow) {
      if (propertyType === 'Lägenhet') {
        formData.floor = floor;
        formData.has_elevator = hasElevator ? 'true' : 'false';
        formData.monthly_fee = monthlyFee;
        formData.balcony_type = balconyType;
        
        // Lägg till lägenhetsfaciliteter
        formData.has_storage = facilities.has_storage ? 'true' : 'false';
        formData.has_bike_room = facilities.has_bike_room ? 'true' : 'false';
        formData.has_laundry_room = facilities.has_laundry_room ? 'true' : 'false';
        formData.has_broadband = facilities.has_broadband ? 'true' : 'false';
      } 
      else if (propertyType === 'Villa' || propertyType === 'Radhus') {
        formData.plot_size = plotSize;
        formData.construction_year = constructionYear;
        if (numberOfFloors) {
          formData.number_of_floors = numberOfFloors;
        }
        formData.has_garage = hasGarage ? 'true' : 'false';
        formData.heating_system = heatingSystem;
        
        // Lägg till villaspecifika faciliteter
        formData.has_garage = facilities.has_garage ? 'true' : 'false';
        formData.has_carport = facilities.has_carport ? 'true' : 'false';
        formData.has_patio = facilities.has_patio ? 'true' : 'false';
        formData.has_parking = facilities.has_parking ? 'true' : 'false';
        formData.has_fireplace = facilities.has_fireplace ? 'true' : 'false';
        formData.has_broadband = facilities.has_broadband ? 'true' : 'false';
      } 
      else if (propertyType === 'Fritidsboende') {
        formData.plot_size = plotSize;
        formData.construction_year = constructionYear;
        if (numberOfFloors) {
          formData.number_of_floors = numberOfFloors;
        }
        formData.has_garage = hasGarage ? 'true' : 'false';
        formData.heating_system = heatingSystem;
        
        // Lägg till fritidshusspecifika faciliteter
        formData.has_municipal_water = facilities.has_municipal_water ? 'true' : 'false';
        formData.has_water_proximity = facilities.has_water_proximity ? 'true' : 'false';
        formData.has_boat_dock = facilities.has_boat_dock ? 'true' : 'false';
        formData.has_winterized = facilities.has_winterized ? 'true' : 'false';
      } else if (propertyType === 'Tomt') {
        formData.has_municipal_water = facilities.has_municipal_water ? 'true' : 'false';
        formData.has_broadband = facilities.has_broadband ? 'true' : 'false';
        formData.has_water_proximity = facilities.has_water_proximity ? 'true' : 'false';
      } else if (propertyType === 'Gård/skog') {
        formData.has_garage = facilities.has_garage ? 'true' : 'false';
        formData.has_municipal_water = facilities.has_municipal_water ? 'true' : 'false';
        formData.has_patio = facilities.has_patio ? 'true' : 'false';
        formData.has_fireplace = facilities.has_fireplace ? 'true' : 'false';
        formData.has_broadband = facilities.has_broadband ? 'true' : 'false';
      } else if (propertyType === 'Övrigt') {
        formData.has_garage = facilities.has_garage ? 'true' : 'false';
        formData.has_municipal_water = facilities.has_municipal_water ? 'true' : 'false';
        formData.has_broadband = facilities.has_broadband ? 'true' : 'false';
      }
    }

    try {
      // Track address input for analytics before submission
      if (address) {
        trackAddressInput(address, 'object_form');
      }

      // Call handleSubmit with the validated form data - images are optional
      if (selectedFiles.length > 0) {
        // Create FormData object for the API call
        const apiFormData = new FormData();
        
        // Add all the form fields to the FormData
        Object.keys(formData).forEach(key => {
          apiFormData.append(key, formData[key]);
        });
        
        // Add all selected files
        selectedFiles.forEach(file => {
          apiFormData.append('files[]', file);
        });
        
        // Call handleSubmit with the files and pass the FormData object
        await handleSubmit(formData, selectedFiles, apiFormData);
      } else {
        // If no files selected, just submit the form data - this is perfectly fine
        await handleSubmit(formData, []);
      }
      
      // Track form submission for analytics
      trackFormSubmission('initial_object_form', {
        property_type: propertyType,
        workflow_type: workflowType,
        has_images: selectedFiles.length > 0
      });
      
      // Clear selected files after successful submission
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Handle validation errors from server
      if (error.response && error.response.data && error.response.data.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach(err => {
          serverErrors[err.field] = err.message;
        });
        setValidationErrors(serverErrors);
      } else if (error.response && error.response.data && error.response.data.message) {
        // Handle specific server error messages
        setImageError(error.response.data.message);
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        setImageError(t('objectForm.validation.timeout'));
      } else if (error.response && error.response.status === 413) {
        setImageError(t('objectForm.validation.fileTooLarge'));
      } else if (error.response && error.response.status === 422) {
        setImageError(t('objectForm.validation.invalidFile'));
      } else if (error.response && error.response.status === 400) {
        setImageError(t('objectForm.validation.badForm'));
      } else if (error.response && error.response.status === 500) {
        setImageError(t('objectForm.validation.server'));
      } else if (!navigator.onLine) {
        setImageError(t('objectForm.validation.offline'));
      } else if (error.name === 'NetworkError' || error.message.includes('Network')) {
        setImageError(t('objectForm.validation.network'));
      } else {
        setImageError(t('objectForm.validation.unexpected'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={submitForm} className="object-form">
        <div className="form-header">
          {!isSlimWorkflow && (
            <div className="step-indicator">
              <span className="step-text">
                {workflowType === 'direct' ? t('objectForm.stepDirect') : t('objectForm.stepAi')}
              </span>
              <div className="step-progress">
                <div className="step-progress-dot active"></div>
                <div className="step-progress-dot"></div>
                {workflowType !== 'direct' && <div className="step-progress-dot"></div>}
              </div>
            </div>
          )}
        </div>
        <h3>{t('objectForm.title')}</h3>
        {/* Basic information section */}
        <div className="form-section">
          {/* Email field - only show for slim workflow when not provided via URL */}
          {isSlimWorkflow && !hideEmailField && (
            <>
              <label htmlFor="email">{t('common.email')}:</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                required
                placeholder={t('objectForm.emailPlaceholder')}
                className={validationErrors.email ? 'input-error' : ''}
              />
              <ValidationErrorMessage fieldName="email" />
            </>
          )}
          <label htmlFor="address">{t('common.address')}:</label>
          <input
            type="text"
            id="address"
            value={address}
            onChange={handleAddressChangeWithValidation}
            required
            className={validationErrors.address ? 'input-error' : ''}
          />
          <ValidationErrorMessage fieldName="address" />
          {/* Show suggestions */}
          {addressSuggestions.length > 0 && (
            <ul className="suggestions-list">
              {addressSuggestions.map((feature) => (
                <li
                  key={feature.id}
                  onClick={() => handleSuggestionClick(feature)}
                >
                  {feature.place_name}
                </li>
              ))}
            </ul>
          )}
          
          <div className="checkbox-group" style={{display: 'none'}}>
            <input
              type="checkbox"
              id="displayContactInfo"
              checked={displayContactInfo}
              onChange={(e) => setDisplayContactInfo(e.target.checked)}
            />
            <label htmlFor="displayContactInfo">
              {t('objectForm.displayContact')}
              <span className="checkbox-description">
                {t('objectForm.displayContactHelp')}
              </span>
            </label>
          </div>
          
          <label htmlFor="propertyType">{t('objectForm.propertyType')}:</label>
          <select
            id="propertyType"
            value={propertyType}
            onChange={handlePropertyTypeChange}
            required
            className={validationErrors.propertyType ? 'input-error' : ''}
          >
            <option value="">{t('common.select')}</option>
            {propertyTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {translatePropertyType(option.value, language)}
              </option>
            ))}
          </select>
          <ValidationErrorMessage fieldName="propertyType" />
          
          {/* Antal rum - visa bara om bostadstyp INTE är Tomt eller Gård/skog */}
          {propertyType !== 'Tomt' && propertyType !== 'Gård/skog' && (
            <>
              <label htmlFor="numberOfRooms">{t('objectForm.rooms')}:</label>
              <select
                id="numberOfRooms"
                value={numberOfRooms}
                onChange={handleNumberOfRoomsChange}
                required={propertyType !== 'Tomt' && propertyType !== 'Gård/skog'}
                className={validationErrors.numberOfRooms ? 'input-error' : ''}
              >
                <option value="">{t('common.select')}</option>
                <option value="1">1 {t('objectForm.room')}</option>
                <option value="2">2 {t('objectForm.room', { count: 2 })}</option>
                <option value="3">3 {t('objectForm.room', { count: 3 })}</option>
                <option value="4">4 {t('objectForm.room', { count: 4 })}</option>
                <option value="5">5 {t('objectForm.room', { count: 5 })}</option>
                <option value="6">{t('objectForm.sixRooms')}</option>
              </select>
              <ValidationErrorMessage fieldName="numberOfRooms" />
            </>
          )}
          
          {/* Boyta - visa för alla bostadstyper */}
          {propertyType && (
            <>
              <label htmlFor="area">{t('objectForm.area')}:</label>
              <input
                type="number"
                id="area"
                value={area}
                onChange={handleAreaChange}
                required
                className={validationErrors.area ? 'input-error' : ''}
              />
              <ValidationErrorMessage fieldName="area" />
            </>
          )}
          

        </div>
        
        {/* Property type specific fields - only show for non-slim workflows */}
        {!isSlimWorkflow && propertyType && (
          <PropertyFields
            propertyType={propertyType}
            facilities={facilities}
            setFacilities={setFacilities}
            floor={floor}
            setFloor={handleFloorChange}
            monthlyFee={monthlyFee}
            setMonthlyFee={handleMonthlyFeeChange}
            balconyType={balconyType}
            setBalconyType={setBalconyType}
            plotSize={plotSize}
            setPlotSize={handlePlotSizeChange}
            constructionYear={constructionYear}
            setConstructionYear={handleConstructionYearChange}
            numberOfFloors={numberOfFloors}
            setNumberOfFloors={setNumberOfFloors}
            heatingSystem={heatingSystem}
            setHeatingSystem={setHeatingSystem}
            validationErrors={validationErrors}
            ValidationErrorMessage={ValidationErrorMessage}
          />
        )}
        
        {/* Description and images section - simplified for slim workflow */}
        {!isSlimWorkflow && (
          <div className="form-section">
            <h3>{t('objectForm.descriptionImages')}</h3>
            <label htmlFor="object">{t('objectForm.description')}:</label>
            <textarea
              id="object"
              value={object}
              onChange={handleObjectChange}
              placeholder={t('objectForm.descriptionPlaceholder')}
              required
              className={validationErrors.object ? 'input-error' : ''}
            />
            <ValidationErrorMessage fieldName="object" />
            
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="hasRenovations"
                checked={hasRenovations}
                onChange={(e) => setHasRenovations(e.target.checked)}
              />
              <label htmlFor="hasRenovations">
                {t('objectForm.renovated')}
                <span className="checkbox-description">
                  {t('objectForm.renovatedHelp')}
                </span>
              </label>
            </div>
            
            {hasRenovations && (
              <>
                <label htmlFor="renovations">{t('objectForm.renovationsLabel')}:</label>
                <textarea
                  id="renovations"
                  value={renovations}
                  onChange={(e) => setRenovations(e.target.value)}
                  placeholder={t('objectForm.renovationsPlaceholder')}
                />
              </>
            )}
            
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="hasSalesRequirements"
                checked={hasSalesRequirements}
                onChange={(e) => setHasSalesRequirements(e.target.checked)}
              />
              <label htmlFor="hasSalesRequirements">
                {t('objectForm.requirements')}
                <span className="checkbox-description">
                  {t('objectForm.requirementsHelp')}
                </span>
              </label>
            </div>
            
            {hasSalesRequirements && (
              <>
                <label htmlFor="salesRequirements">{t('objectForm.requirementsLabel')}:</label>
                <textarea
                  id="salesRequirements"
                  value={salesRequirements}
                  onChange={(e) => setSalesRequirements(e.target.value)}
                  placeholder={t('objectForm.requirementsPlaceholder')}
                />
              </>
            )}
            
            <label>{t('objectForm.uploadImages')}:</label>
            <UnifiedFileUpload
              existingImages={uploadedImages}
              onImagesUpdate={handleImagesUpdate}
              onFilesChange={handleFilesChange}
              onPreviewUrlsChange={handlePreviewUrlsChange}
              onImageClick={handleImageClick}
              allowMultiple={true}
              maxFiles={10}
              delayUpload={true}
              data_id={null}
            />
            {imageError && <div className="validation-error">{imageError}</div>}
          </div>
        )}

        
        <div className="form-buttons">
          <button 
            type="submit" 
            className={`button ${isLoggedIn ? 'primary-button' : 'bankid-button'}`} 
            disabled={loading}
          >
            <FontAwesomeIcon icon={faPlusCircle} />
            <span className="button-text">{workflowType === 'direct' ? t('common.createAd') : isSlimWorkflow ? t('common.createAd') : t('common.next')}</span>
          </button>
          <Link to="/" className="button home-button">
            <FontAwesomeIcon icon={faHome} />
            <span className="button-text">{t('common.home')}</span>
          </Link>
        </div>

        {loading && <SpinnerOverlay creatingAd={workflowType === 'direct'} />}
      </form>

      {/* Image Modal Component */}
      {selectedImage && (
        <ImageModal
          selectedImage={selectedImage}
          selectedImageIndex={selectedImageIndex}
          totalImages={getAllImages().length}
          onClose={handleCloseModal}
          onPrevImage={handlePrevImage}
          onNextImage={handleNextImage}
        />
      )}
    </>
  );
};

export default ObjectForm;
